/**
 * useDocumentTitle — reflète la localisation courante dans le titre d'onglet
 * navigateur (SAAS-1078).
 *
 * @remarks
 * Priorité au fil d'Ariane déjà publié par la vue courante (`useBreadcrumb`
 * → store partagé, casse d'origine) : c'est la source la plus précise là où
 * elle existe. Repli sur le `navigation` du manifest propriétaire du contexte
 * (minuscule) quand la vue ne publie rien — c'est le cas de toutes les pages
 * de premier niveau, et de tout `admin/*` (aucune vue admin ne publie de fil
 * d'Ariane aujourd'hui : `AdminNavView` est une nav interne indépendante, non
 * branchée sur `useBreadcrumb`).
 *
 * Aucune nouvelle table `Context → libellé` dans les deux cas — même raison
 * que `useFocusOnRouteChange` réutilise le `<h1>` des vues plutôt qu'un
 * registre central (cf. SAAS-884). Les sous-features admin (skill-up,
 * llm-config, users…) ne déclarent pas de `navigation` propre — seul
 * `admin-evaluations` porte le lien partagé `'Admin'` de la sidebar. On les
 * rattache via la convention d'`id` du manifest (`admin-<sous-section>`),
 * déjà stable et unique, plutôt que d'ajouter un champ à chaque manifest pour
 * ce seul besoin.
 */

import { useEffect } from 'react';
import {
  featureRegistry,
  type AppContext,
  type Context,
  type FeatureManifest,
} from '@/app/registry';
import { useBreadcrumbStore } from '@/shared/components/breadcrumb/use-breadcrumb-store';

const APP_NAME = 'Alt';
const ADMIN_ID_PREFIX = 'admin-';

/**
 * Même critère que `getCenterView` : le premier manifest actif qui a
 * effectivement un `centerView` pour ce contexte. Nécessaire pour ignorer les
 * manifests transverses (ex. `admin-copilot`, posé sur presque tous les
 * contextes admin pour un simple onglet de panneau, sans `centerView`).
 */
function findOwningManifest(context: Context, appCtx: AppContext): FeatureManifest | undefined {
  const activeFeatures = featureRegistry.getActiveFeatures(context, appCtx);
  return activeFeatures.find((m) => m.centerView?.[context] !== undefined) ?? activeFeatures[0];
}

function resolveFallbackSections(context: Context, appCtx: AppContext): readonly string[] {
  const owning = findOwningManifest(context, appCtx);
  if (!owning) return [];

  // Vérifié avant `navigation` : `admin-evaluations` porte le lien partagé
  // `'Admin'` de la sidebar en plus d'être lui-même une sous-section — sans
  // cette priorité, il retomberait sur `[Admin]` seul et perdrait
  // `evaluations`, contrairement à toutes les autres sous-sections admin.
  if (owning.id.startsWith(ADMIN_ID_PREFIX)) {
    const adminLabel = featureRegistry
      .getAllNavigationLinks(appCtx)
      .find((link) => link.id === 'admin')?.label;
    const subLabel = owning.id.slice(ADMIN_ID_PREFIX.length);

    return (adminLabel ? [adminLabel, subLabel] : [subLabel]).map((s) => s.toLowerCase());
  }

  const ownLabel = owning.navigation?.[0]?.label;
  return ownLabel ? [ownLabel.toLowerCase()] : [];
}

export function useDocumentTitle(context: Context, appCtx: AppContext): void {
  const breadcrumbItems = useBreadcrumbStore((s) => s.items);

  useEffect(() => {
    const sections =
      breadcrumbItems.length > 0
        ? breadcrumbItems.map((item) => item.label)
        : resolveFallbackSections(context, appCtx);

    document.title = sections.length > 0 ? `${APP_NAME} - ${sections.join(' - ')}` : APP_NAME;
  }, [breadcrumbItems, context, appCtx]);
}
